import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  bulkCreateSlots,
  createSlot,
  deleteSlot,
  listAvailableSlots,
} from '../../api/bookings.js'
import { useAuth } from '../../auth/AuthProvider.jsx'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'

/**
 * Doctor — Availability editor.
 *
 * Slot-by-slot opt-in pattern (mirrors the legacy native app per
 * Issac's 2026-05-07 vision). Doctor picks a single date; the page
 * renders a fixed grid of 15-minute windows from 07:00 to 19:00
 * (configurable below). Each cell is one of three states:
 *
 *   • OFF       — no saved slot for that minute. Tap to create one.
 *   • AVAILABLE — slot saved on the server, no active booking. Tap to delete.
 *   • BOOKED    — slot saved AND a patient has claimed it. Tap is a
 *                 no-op (the cancel-then-delete dance lives elsewhere).
 *
 * Mutations fire immediately on tap (optimistic UI via React Query
 * cache update). The page also offers a "Open all 7-5" preset that
 * bulk-creates a full working day in one request — saves the doctor
 * 36 individual taps for the common case.
 */

// Window the grid renders. 12 hours × 4 quarters/hour = 48 cells.
// Doctors who work non-standard hours (e.g. on-call shift) can simply
// scroll past — we don't try to be a full calendar app, the goal is
// "tap your available windows for tomorrow before clinic opens."
const HOUR_START = 7
const HOUR_END = 19
const STEP_MIN = 15

export default function AvailabilityPage() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const navigate = useNavigate()
  // Tracks whether anything in flight is still saving — disables the
  // green Save-and-return button until the queue is clear, so a tap-
  // happy doctor can't navigate away mid-write.
  const anyMutationPending = (m) => Boolean(m?.isPending)

  // Default to tomorrow — most doctors set tomorrow's slots at end-of-day.
  const [date, setDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    return d.toISOString().slice(0, 10)
  })

  const doctorId = user?.profile?.id

  // ---- Server fetch — slots already saved for this date ---------------
  // We pull the FULL day (no available_only filter) so we know which
  // slots are booked vs free. The grid colours each cell from this.
  // The FE's `date` is a local-day key (YYYY-MM-DD). The server stores
  // and returns UTC datetimes. Without conversion, "Mon 08 May" in PNG
  // (+10) bleeds across two UTC days — slots that the doctor scheduled
  // for 07:00 PNG (= 21:00 UTC the previous day) wouldn't appear in the
  // grid because we'd be asking the server for slots strictly between
  // 2026-05-08T00:00 UTC and 2026-05-08T23:59 UTC.
  //
  // Fix: convert the local-day to its UTC bounds before querying.
  const utcRange = useMemo(() => localDayToUtcRange(date), [date])

  const slotsQ = useQuery({
    queryKey: ['my-availability', doctorId, date],
    queryFn: () => listAvailableSlots({
      doctorId,
      dateFrom: utcRange.start,
      dateTo:   utcRange.end,
      availableOnly: false,
    }),
    enabled: Boolean(doctorId && date),
    staleTime: 5_000,
  })

  // Index server rows by their HH:MM-of-day key for O(1) lookup in
  // each cell render.
  const savedByKey = useMemo(() => {
    const map = new Map()
    for (const row of slotsQ.data || []) {
      const d = new Date(row.slot_at)
      const key = fmtHHMM(d)
      map.set(key, row)
    }
    return map
  }, [slotsQ.data])

  // ---- Build the 48-cell grid ------------------------------------------
  const cells = useMemo(() => {
    const out = []
    for (let h = HOUR_START; h < HOUR_END; h++) {
      for (let m = 0; m < 60; m += STEP_MIN) {
        out.push({ hh: h, mm: m, key: `${pad2(h)}:${pad2(m)}` })
      }
    }
    return out
  }, [])

  // ---- Mutations -------------------------------------------------------
  const create = useMutation({
    mutationFn: ({ slotAt }) => createSlot({ slotAt }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-availability', doctorId, date] }),
  })
  const remove = useMutation({
    mutationFn: ({ id }) => deleteSlot(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-availability', doctorId, date] }),
  })
  const bulk = useMutation({
    mutationFn: (slots) => bulkCreateSlots(slots),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-availability', doctorId, date] }),
  })

  function toggleCell(cell) {
    const existing = savedByKey.get(cell.key)
    if (existing) {
      if (existing.is_booked) return  // locked — can't delete a booked slot
      remove.mutate({ id: existing.id })
      return
    }
    // Build the UTC ISO from the doctor's local-time intent. Without
    // this, "07:00 on 8 May" gets stored as 07:00 UTC instead of the
    // 07:00 PNG the doctor meant.
    const slotAt = localDateTimeToUtcIso(date, cell.hh, cell.mm)
    create.mutate({ slotAt })
  }

  function openAllStandard() {
    // 7:00 → 17:00, every 15 min. Each slot's wall-clock time is
    // anchored to the doctor's local timezone — `.toISOString()` then
    // converts to the UTC string the API expects.
    const slots = []
    for (let h = 7; h < 17; h++) {
      for (let m = 0; m < 60; m += STEP_MIN) {
        slots.push({ slotAt: localDateTimeToUtcIso(date, h, m) })
      }
    }
    bulk.mutate(slots)
  }

  if (!user || !doctorId) {
    return <div className="p-6 text-sm text-slate-400">Loading…</div>
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="My availability"
          subtitle={
            'Tap each 15-minute window you can see patients on the chosen date. ' +
            'Patients see only the slots you tap on; everything else means you\'re unavailable. ' +
            'Bookings appear on the same grid in amber and lock the slot until cancelled.'
          }
        />

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <label className="text-xs text-slate-600 flex items-center gap-2">
            <span>Date</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={openAllStandard}
            disabled={bulk.isPending}
            className="text-xs bg-wantok-50 hover:bg-wantok-100 border border-wantok-200 text-wantok-800 rounded px-3 py-1.5 disabled:opacity-60"
            title="Open every 15-minute slot from 07:00 to 17:00 in one tap"
          >
            {bulk.isPending ? 'Opening…' : 'Open standard day (7:00 – 17:00)'}
          </button>
          {bulk.data && (
            <span className="text-[11px] text-slate-500">
              Added {bulk.data.created_count}{bulk.data.skipped_count > 0 ? ` (skipped ${bulk.data.skipped_count} duplicates)` : ''}
            </span>
          )}
        </div>

        {(slotsQ.error || create.isError || remove.isError || bulk.isError) && (
          <div className="mb-3">
            <ErrorBanner error={slotsQ.error || create.error || remove.error || bulk.error} />
          </div>
        )}

        {slotsQ.isLoading ? (
          <div className="text-sm text-slate-400">Loading slots…</div>
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5">
            {cells.map((cell) => {
              const saved = savedByKey.get(cell.key)
              const state = !saved
                ? 'off'
                : saved.is_booked
                  ? 'booked'
                  : 'available'
              return (
                <button
                  key={cell.key}
                  type="button"
                  onClick={() => toggleCell(cell)}
                  disabled={state === 'booked' || create.isPending || remove.isPending}
                  className={
                    'text-[12px] rounded-md py-1.5 border transition ' +
                    (state === 'available'
                      ? 'bg-wantok-700 border-wantok-700 text-white hover:bg-wantok-800'
                      : state === 'booked'
                        ? 'bg-amber-100 border-amber-300 text-amber-900 cursor-not-allowed'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-wantok-400 hover:text-wantok-700')
                  }
                  title={
                    state === 'booked'
                      ? 'Booked — patient has reserved this slot. Cancel the booking to free it.'
                      : state === 'available'
                        ? 'Tap to remove this slot from your availability.'
                        : 'Tap to mark this slot available for booking.'
                  }
                >
                  {cell.key}
                </button>
              )
            })}
          </div>
        )}

        {/* Footer — legend on the left, Save & return on the right. */}
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          {/* Legend — three squares matching the cell colours. */}
          <div className="flex flex-wrap items-center gap-4 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-white border border-slate-200" />
              Off (not offered)
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-wantok-700 border border-wantok-700" />
              Available (open for booking)
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-amber-100 border border-amber-300" />
              Booked (locked until cancelled)
            </span>
          </div>

          {/* Save & return — slots auto-save as the doctor taps each
              cell, so this button is really "I'm done, take me back to
              the dashboard" without forcing them to use the browser
              back arrow. Disabled while any in-flight create / delete /
              bulk write is still resolving so we don't navigate away
              mid-mutation. */}
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-slate-400 italic">
              Changes save automatically
            </span>
            <button
              type="button"
              onClick={() => navigate('/doctor')}
              disabled={
                anyMutationPending(create)
                || anyMutationPending(remove)
                || anyMutationPending(bulk)
              }
              className={
                'inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-bold border-2 transition ' +
                'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 ' +
                'border-emerald-600 hover:border-emerald-700 ' +
                'text-white disabled:opacity-60 disabled:cursor-not-allowed'
              }
              title="Slots are saved as you tap. This takes you back to the doctor dashboard."
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Save &amp; return to dashboard
            </button>
          </div>
        </div>
      </Card>
    </div>
  )
}

// ---- Helpers ---------------------------------------------------------------

function pad2(n) { return String(n).padStart(2, '0') }
function fmtHHMM(d) { return `${pad2(d.getHours())}:${pad2(d.getMinutes())}` }

/**
 * Convert a local-day key (YYYY-MM-DD) plus a local hour+minute into
 * the UTC ISO string the API expects. Constructing the Date with the
 * `(year, monthIndex, day, hour, minute)` form anchors it to the
 * browser's local timezone; `.toISOString()` then renders UTC.
 */
function localDateTimeToUtcIso(yyyymmdd, hh, mm) {
  const [y, mo, d] = yyyymmdd.split('-').map(Number)
  return new Date(y, mo - 1, d, hh, mm, 0).toISOString()
}

/**
 * Convert a local-day key (YYYY-MM-DD) into the UTC ISO bounds that
 * cover the entire local day. PNG (+10) means a local-Monday spans
 * 14:00 Sunday UTC through 13:59 Monday UTC. Sending the unzoned
 * `${date}T00:00:00` literal would clip the early-morning slots.
 */
function localDayToUtcRange(yyyymmdd) {
  const [y, mo, d] = yyyymmdd.split('-').map(Number)
  const start = new Date(y, mo - 1, d, 0, 0, 0)
  const end   = new Date(y, mo - 1, d, 23, 59, 59)
  return { start: start.toISOString(), end: end.toISOString() }
}
